import { isEmpty, map } from 'lodash'
import moment from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import cn from 'classnames'

import { Loading } from 'civic-champs-shared'
import { linkify } from 'civic-champs-shared/utils/linkify'
import { FeatureEnabledState, withFeatureEnabled } from 'contexts/FeatureContext'
import { createMarkup } from 'civic-champs-shared/core/utils/helpers'
import ENDPOINT_ROUTES from 'constants/APIs'
import { BROWSER_TIMEZONE } from 'constants/timezone'
import { ROUTE_NAMES } from 'constants/route-names'
import { encodeOccurrence } from 'helpers/encodeOccurrence'
import { StackLink } from 'navigation/StackLink'

//css copied from https://www.civicchamps.com/event/waterford-foundation-2026-golf-cart-driver-shuttle-support
const useStyles = makeStyles({
  root: {
    marginTop: '60px',
    paddingBottom: '25px',
    textAlign: 'center', //TODO the original uses a @media breakpoint to left align
    fontSize: '20px',
    '& p': {
      marginBottom: '16px',
      fontFamily: 'Nunito, sans-serif',
      fontSize: '16px',
      lineHeight: '24px',
    },
  },
  'container-97': {
    opacity: 1,
    backgroundColor: 'rgba(245, 244, 244, 0.25)',
    marginBottom: '60px',
    paddingTop: '20px',
    paddingLeft: '20px',
    paddingRight: '20px',
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 5px',
    borderRadius: '20px',
  },
  'w-layout-blockcontainer': {
    maxWidth: '940px',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'block',
  },
  'heading-105': {
    textAlign: 'center',
    marginBottom: '60px',
    borderBottom: '1px rgba(54, 27, 27, 0.24)',
    marginTop: '20px', // copied from h2
    paddingTop: '0px',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '32px',
    fontWeight: 600,
    lineHeight: '52px',
  },
  'collection-item-21': {
    marginBottom: '0px',
    paddingBottom: '40px',
    paddingLeft: '20px',
  },
  'text-block-145': {
    paddingTop: '8px',
    paddingBottom: '25px',
    paddingLeft: '5px',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '24px',
    fontWeight: 700,
    borderRadius: '11px',
  },
  'div-block-163': {
    opacity: '1',
    backgroundColor: 'rgba(212, 212, 212, 0.2)',
    marginLeft: '20px',
    marginRight: '20px',
    paddingTop: '10px',
    paddingBottom: '25px',
    paddingLeft: '20px',
    borderRadius: '20px',
  },
  'heading-109': {
    marginTop: '10px',
    fontSize: '22px',
    fontWeight: 700,
  },
  'div-block-164': {
    columnGap: '5px',
    rowGap: '5px',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: '10px',
    paddingBottom: '10px',
    display: 'flex',
  },
  'heading-106': {
    color: 'var(--black)',
    marginTop: '0px',
    marginBottom: '0px',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    letterSpacing: '1px', //TODO from h4 in base?
    textTransform: 'uppercase',
    fontSize: '15px',
    lineHeight: '20px',
  },
  float: {
    float: 'left',
    aspectRatio: 'auto',
    paddingRight: '5px',
    position: 'relative',
  },
  'paragraph-33': {
    marginBottom: '0px !important',
  },
  'div-block-165': {
    columnGap: '5px',
    rowGap: '5px',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: '10px',
    display: 'flex',
  },
  'rich-text-block-25': {
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: '15px',
    display: 'block',
  },
  'div-block-168': {
    display: 'block',
  },
  'rich-text-block-24': {
    //TODO has @media query associated with it at 1280px breakpoint
    textAlign: 'left',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: '-2px',
    marginBottom: '10px',
    marginLeft: '0px',
    lineHeight: '12px',
    display: 'block',
  },
  'button-23': {
    paddingLeft: '30px',
    paddingRight: '30px',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '16px',
    fontWeight: 700,
    borderRadius: '20px',
  },
  'w-button': {
    color: 'rgb(255, 255, 255)',
    lineHeight: 'inherit',
    cursor: 'pointer',
    backgroundColor: '#4985df',
    //backgroundColor: 'rgb(56, 152, 236)',
    display: 'inline-block',
    borderWidth: '0px',
    borderStyle: 'none',
    borderColor: 'currentcolor',
    borderImage: 'none',
    padding: '9px 15px',
    textDecoration: 'none',
  },
})

interface OpportunityResponse {
  opportunityId: number
  organization: { timeZone: string }
  timeZone?: string
  name: string
  description?: string
  startsAt: Date | string
  endsAt: Date | string
  address: string
  state?: string
  zip?: string
  city?: string
  locationDetails?: string
  locationIsAddress: boolean
  relativeSchedulerLink?: string
}

interface SuperEvent {
  organizationId: number
  title: string
  description?: string
  events: OpportunityResponse[]
}

interface Props {
  match: any
  history?: any
  location?: any
  currentUser?: any
  getJSON: (endpoint: string) => Promise<any>
  setOpenLogin?: (isOpen: boolean, shouldRedirectToRegister?: boolean) => void
  featureEnabledState?: FeatureEnabledState
}

const SuperEventScene = (props: Props) => {
  const { history, match, getJSON } = props
  const { stableLinkCode } = match.params

  const [loading, setLoading] = useState(false)
  const [superEvent, setSuperEvent] = useState<SuperEvent>()

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        const apiUrl = ENDPOINT_ROUTES.SuperEvent.getByLink(stableLinkCode)
        const result = await getJSON(apiUrl)

        if (result.error) {
          history.push(ROUTE_NAMES.notFound())
          return
        }

        setSuperEvent(result)
      } finally {
        setLoading(false)
      }
    })()
  }, [getJSON, stableLinkCode, history])

  const classes = useStyles()

  if (loading || superEvent == null) return <Loading />

  let prevDate: moment.Moment | null = null
  let currDate: moment.Moment | null = null

  return (
    <section className={classes.root}>
      <div className={cn(classes['w-layout-blockcontainer'], classes['container-97'])}>
        <h2 className={classes['heading-105']}>{superEvent?.title}</h2>
        {/* TODO optional description, what does markup look like? */}
        <div
          style={{ textAlign: 'center' }}
          className={classes['rich-text-block-24']}
          dangerouslySetInnerHTML={createMarkup(superEvent?.description)}
        />
        <div>
          <div>
            {map(superEvent?.events, occurrence => {
              const {
                opportunityId,
                organization,
                name,
                description,
                startsAt,
                endsAt,
                address,
                city,
                state,
                zip,
                locationIsAddress,
                locationDetails,
                relativeSchedulerLink,
              } = occurrence

              const timeZone = occurrence.timeZone || organization.timeZone || BROWSER_TIMEZONE
              prevDate = currDate

              let fullAddress
              if (locationIsAddress) {
                fullAddress =
                  city != null && address.includes(city) ? address : [address, city, [state, zip].join(' ')].join(', ')
              } else {
                fullAddress = linkify(locationDetails as string)
              }

              const encodedId = encodeOccurrence(opportunityId, startsAt, timeZone)
              const eventUrl =
                relativeSchedulerLink ?? ROUTE_NAMES.occurrenceDetails(superEvent.organizationId, encodedId)

              currDate = moment(startsAt).tz(timeZone)

              return (
                <div role="listitem" className={classes['collection-item-21']}>
                  {!prevDate?.isSame(currDate, 'day') && (
                    <div style={{ color: '#4985df' }} className={classes['text-block-145']}>
                      {currDate.format('dddd LL')}
                    </div>
                  )}
                  <div className={classes['div-block-163']}>
                    <h3 id={encodedId} style={{ color: '#4985df' }} className={classes['heading-109']}>
                      {name}
                    </h3>
                    <div className={classes['div-block-164']}>
                      <h4 className={classes['heading-106']}>Time:</h4>
                      <p className={classes['paragraph-33']}>{currDate.format('h:mm a')}</p>
                      <p className={classes['paragraph-33']}>to</p>
                      <p className={classes['paragraph-33']}>{moment(endsAt).tz(timeZone).format('h:mm a z')}</p>
                    </div>
                    <div className={classes['div-block-165']}>
                      <h4 className={classes['heading-106']}>location:&nbsp;</h4>
                      <div className={classes['rich-text-block-25']}>
                        <p>{fullAddress}</p>
                      </div>
                    </div>
                    {!isEmpty(description) && (
                      <div className={classes['div-block-168']}>
                        <h4 className={cn(classes['heading-106'], classes.float)}>Event Description:</h4>
                        <div
                          className={classes['rich-text-block-24']}
                          dangerouslySetInnerHTML={createMarkup(description)}
                        />
                      </div>
                    )}
                    <StackLink
                      to={eventUrl}
                      backLabel={superEvent.title}
                      className={cn(classes['button-23'], classes['w-button'])}
                    >
                      Register
                    </StackLink>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

export default withFeatureEnabled(SuperEventScene)
